/**
 * Iter 220d — Wallet Transactions (Admin)
 * Lives under Finance → Wallet Transactions. Shows every customer wallet
 * ledger row (recharges, debits, refunds, adjustments) with joined
 * customer name/phone, DD-MM-YYYY dates, type & status filters,
 * date range, and a "Reconcile pending" button that pings Razorpay
 * for any `pending recharge_gateway` rows and either credits the
 * wallet (if the payment was actually captured) or marks it expired
 * after 24 h.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowClockwise, MagnifyingGlass, FunnelSimple, X,
  CaretUp, CaretDown, Coins, CheckCircle, Warning,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  completed: "bg-[#D4E0D1] text-[#1A3317]",
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  pending_verification: "bg-[#F9E4B7] text-[#7A5A11]",
  rejected: "bg-[#F4C7B9] text-[#7A2E18]",
  expired: "bg-[#E5E2D9] text-[#5C5A55]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function WalletTransactions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (kind) p.set("kind", kind);
      if (status) p.set("status", status);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (sort) p.set("sort", sort);
      const r = await api.get(`/admin/wallet/txns?${p}`);
      setItems(r.data.txns || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status, dateFrom, dateTo, sort]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const clearFilters = () => {
    setQ(""); setKind(""); setStatus(""); setDateFrom(""); setDateTo(""); setSort("-created_at");
  };

  const reconcile = async () => {
    if (!window.confirm(
      "Re-check every pending Razorpay recharge and auto-credit wallets " +
      "for payments that were captured but never verified?\n\n" +
      "This is safe to run any time — it's idempotent."
    )) return;
    try {
      setReconciling(true);
      const r = await api.post("/admin/wallet/reconcile");
      const { checked = 0, credited = 0, expired = 0, still_pending = 0 } = r.data || {};
      toast.success(
        `Checked ${checked} · Credited ${credited} · Expired ${expired} · Still pending ${still_pending}`,
        { duration: 6000 },
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconciling(false);
    }
  };

  const kindOptions = useMemo(() => {
    const s = new Set([
      "recharge_gateway", "recharge_staff", "referral",
      "debit_service", "refund", "refund_cancel", "adjustment",
    ]);
    items.forEach((r) => r.kind && s.add(r.kind));
    return Array.from(s);
  }, [items]);

  const toggleSort = (key) => setSort(sort === `-${key}` ? key : `-${key}`);
  const sortIcon = (key) => sort === key
    ? <CaretUp size={11} weight="bold" className="inline ml-1" />
    : sort === `-${key}` ? <CaretDown size={11} weight="bold" className="inline ml-1" /> : null;

  const pendingCount = items.filter((r) => r.status === "pending" && r.kind === "recharge_gateway").length;

  return (
    <AppShell title="Wallet Transactions" subtitle="Module 12 · Finance">
      {/* Reconcile banner — visible whenever any gateway pending row exists */}
      {pendingCount > 0 && (
        <div className="mb-4 rounded-md border border-[#F9E4B7] bg-[#FFF8E6] px-5 py-3 flex items-center gap-3" data-testid="wallet-reconcile-banner">
          <Warning size={18} weight="fill" className="text-[#B58412] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-[#7A5A11]">
              {pendingCount} pending gateway recharge{pendingCount > 1 ? "s" : ""}
            </div>
            <div className="text-[11px] text-[#7A5A11]">
              Reconcile pulls the latest status from Razorpay and credits any wallet that was captured but never verified.
            </div>
          </div>
          <button onClick={reconcile} disabled={reconciling} data-testid="wallet-reconcile-btn"
                  className="h-9 px-3 bg-[#B58412] hover:bg-[#7A5A11] disabled:opacity-60 text-white rounded-md text-xs font-bold inline-flex items-center gap-1">
            <ArrowClockwise size={13} weight="bold" className={reconciling ? "animate-spin" : ""} />
            {reconciling ? "Reconciling…" : "Reconcile now"}
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E5E2D9] rounded-md">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex flex-wrap items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search customer / phone / order / payment id"
                   data-testid="wtxn-search"
                   className="h-9 pl-8 pr-3 w-80 border border-[#E5E2D9] rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#DC2626]/20" />
          </div>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="wtxn-filter-kind"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All types</option>
            {kindOptions.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="wtxn-filter-status"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex items-center gap-1 border border-[#E5E2D9] rounded-md pl-2 pr-1 h-9">
            <FunnelSimple size={12} weight="bold" className="text-[#8A8780]" />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   data-testid="wtxn-filter-date-from" className="h-8 text-xs bg-transparent focus:outline-none" />
            <span className="text-[#8A8780] text-xs">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   data-testid="wtxn-filter-date-to" className="h-8 text-xs bg-transparent focus:outline-none" />
          </div>
          {(q || kind || status || dateFrom || dateTo) && (
            <button onClick={clearFilters} data-testid="wtxn-clear-filters"
                    className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white text-[#5C5A55] inline-flex items-center gap-1 hover:bg-[#FAFAF7]">
              <X size={11} weight="bold" /> Clear
            </button>
          )}
          {pendingCount === 0 && (
            <button onClick={reconcile} disabled={reconciling} data-testid="wtxn-reconcile-btn-inline"
                    className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white text-[#5C5A55] inline-flex items-center gap-1 hover:bg-[#FAFAF7]">
              <ArrowClockwise size={12} weight="bold" className={reconciling ? "animate-spin" : ""} />
              {reconciling ? "Reconciling…" : "Reconcile Razorpay"}
            </button>
          )}
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]" data-testid="wtxn-record-count">
            {loading ? "Loading…" : `${items.length} records`}
          </div>
        </div>

        <table className="w-full text-sm" data-testid="wtxns-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3 text-right cursor-pointer select-none"
                  onClick={() => toggleSort("amount")} data-testid="wtxn-sort-amount">
                Amount{sortIcon("amount")}
              </th>
              <th className="px-6 py-3">Balance</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Reference</th>
              <th className="px-6 py-3 cursor-pointer select-none"
                  onClick={() => toggleSort("created_at")} data-testid="wtxn-sort-date">
                Date{sortIcon("created_at")}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-16 text-center text-[#8A8780]">
                {loading ? "Loading wallet transactions…" : "No wallet transactions match your filters."}
              </td></tr>
            )}
            {items.map((t, i) => {
              const amt = Number(t.amount || 0);
              return (
                <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`wtxn-row-${t.id}`}>
                  <td className="px-6 py-3">
                    <div className="text-xs font-semibold text-[#1A1A1A] truncate max-w-[220px]">
                      {t.customer_name || "—"}
                    </div>
                    <div className="text-[10px] text-[#8A8780] font-mono">
                      {t.customer_phone || t.customer_code || t.customer_id?.slice(0, 8)}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-xs capitalize whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <Coins size={12} weight="fill" className={amt >= 0 ? "text-[#15803D]" : "text-[#DC2626]"} />
                      {(t.kind || "").replace(/_/g, " ")}
                    </div>
                  </td>
                  <td className={`px-6 py-3 font-mono text-right text-xs font-bold ${amt >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                    {amt >= 0 ? "+" : ""}₹{Math.abs(amt).toLocaleString("en-IN")}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">
                    {t.balance_after != null ? `₹${Number(t.balance_after).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[t.status] || "bg-[#E5E2D9] text-[#5C5A55]"}`}>
                      {(t.status || "").replace(/_/g, " ")}
                    </span>
                    {t.source === "razorpay_webhook" && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.18em] text-[#15803D]">
                        <CheckCircle size={9} weight="fill" /> webhook
                      </span>
                    )}
                    {t.source === "razorpay_reconciler" && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.18em] text-[#B58412]">
                        <ArrowClockwise size={9} weight="fill" /> reconciled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 font-mono text-[10px] text-[#5C5A55] max-w-[220px]">
                    {t.razorpay_payment_id ? <div className="truncate">pay · {t.razorpay_payment_id}</div>
                      : t.razorpay_order_id ? <div className="truncate">order · {t.razorpay_order_id}</div>
                      : t.invoice_nos?.length ? <div className="truncate">{t.invoice_nos.join(", ")}</div>
                      : t.transaction_id ? <div className="truncate">{t.transaction_mode} · {t.transaction_id}</div>
                      : "—"}
                    {t.notes && <div className="truncate text-[#8A8780] italic">{t.notes}</div>}
                  </td>
                  <td className="px-6 py-3 text-xs">
                    <div className="font-mono text-[#1A1A1A]" data-testid={`wtxn-date-${t.id}`}>
                      {fmtDate(t.created_at)}
                    </div>
                    <div className="text-[10px] text-[#8A8780]">{fmtTime(t.created_at)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
